import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { gameService } from '../../services/games';
import { INVALID_WORD, INCOMPLETE_ANSWER } from '../../constants/game';

const initialState = {
  targetWord: 'HELLO', // Default hardcoded word
  currentGuess: Array(5).fill(''),
  guesses: [],
  hasWon: false,
  invalidWord: false,
  activeTileIndex: 0,
  maxHints: 2,
  hintsUsed: 0,
  gameStats: {
    completed: false,
    guessCount: 0,
  },
  loading: false,
  error: null,
};

/**
 * Async thunk for submitting a guess
 * Delegates validation to the game service and returns the result
 */
export const submitGuess = createAsyncThunk(
  'learndleWord/submitGuess',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { level } = state.level;
      const { currentGuess, targetWord } = state.learndleWord;
      const guess = currentGuess.join('');

      // For incrementAttempts in checkAnswer, we need the child database ID
      const childId = state.childProfile.childProfile?.id || state.auth.userId;

      // Delegate all validation to the game service
      const result = await gameService.learndleWord.checkAnswer({
        guess,
        targetWord,
        levelId: level?.id,
        childId,
      });

      return {
        guess: guess.toUpperCase(),
        result, // Contains: validAttempt, isCorrect, reason
      };
    } catch (error) {
      return rejectWithValue({ type: 'error', message: error.message });
    }
  }
);

/**
 * Async thunk for using a hint
 * Updates UI via showHint reducer and persists hints_used via service
 */
export const applyHint = createAsyncThunk(
  'learndleWord/applyHint',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const { hintsUsed, maxHints, hasWon } = state.learndleWord;
      if (hintsUsed >= maxHints || hasWon) {
        return { skipped: true };
      }

      // First update the UI
      dispatch(showHint());

      // Persist to backend
      const childId = state.childProfile.childProfile?.id || state.auth.userId;
      const levelId = state.level.level?.id;
      if (!childId || !levelId) {
        return { skipped: true };
      }
      await gameService.learndleWord.incrementHintsUsed({ childId, levelId });

      return { persisted: true };
    } catch (error) {
      return rejectWithValue({ type: 'error', message: error.message });
    }
  }
);

export const learndleWordSlice = createSlice({
  name: 'learndleWord',
  initialState,
  reducers: {
    setTargetWord: (state, action) => {
      const newWord = action.payload.toUpperCase();
      state.targetWord = newWord;
      state.currentGuess = Array(newWord.length).fill('');
      state.guesses = [];
      state.hasWon = false;
      state.invalidWord = false;
      state.activeTileIndex = 0;
      state.hintsUsed = 0;
      state.gameStats = {
        completed: false,
        guessCount: 0,
      };
    },

    setCurrentGuess: (state, action) => {
      state.currentGuess = action.payload;
    },

    setActiveTileIndex: (state, action) => {
      state.activeTileIndex = action.payload;
    },

    setInvalidWord: (state, action) => {
      state.invalidWord = action.payload;
    },

    handleKeyPress: (state, action) => {
      const key = action.payload;

      if (state.hasWon) return; // Ignore input if the game is won

      if (key === 'ENTER') {
        // Validation will be handled by submitGuess
        return;
      } else if (key === 'BACKSPACE') {
        // If current active tile is empty, move backward to find a tile with content
        if (!state.currentGuess[state.activeTileIndex]) {
          // Current tile is empty, find the previous non-empty tile
          for (let i = state.activeTileIndex - 1; i >= 0; i--) {
            if (state.currentGuess[i]) {
              state.currentGuess[i] = '';
              state.activeTileIndex = i;
              return;
            }
          }
        } else {
          // Current tile has content, just clear it and keep focus there
          state.currentGuess[state.activeTileIndex] = '';
        }
      } else {
        // Handle letter input
        // Find first empty tile starting from active tile
        let indexToChange = state.activeTileIndex;

        // If current active tile is filled, find the next empty tile
        if (state.currentGuess[indexToChange]) {
          for (let i = indexToChange + 1; i < state.currentGuess.length; i++) {
            if (!state.currentGuess[i]) {
              indexToChange = i;
              break;
            }
          }
        }

        // If we found an empty tile
        if (
          indexToChange < state.currentGuess.length &&
          !state.currentGuess[indexToChange]
        ) {
          state.currentGuess[indexToChange] = key;

          // Move to next tile if available
          if (indexToChange < state.currentGuess.length - 1) {
            state.activeTileIndex = indexToChange + 1;
          }
        }
      }
    },

    showHint: (state) => {
      if (state.hintsUsed >= state.maxHints || state.hasWon) {
        return; // No more hints available or game is won
      }

      // Get all letters from the target word
      const targetLetters = state.targetWord.split('');

      // Create a letter map to track all positions of each letter
      const letterMap = {};
      targetLetters.forEach((letter, index) => {
        if (!letterMap[letter]) {
          letterMap[letter] = [];
        }
        letterMap[letter].push(index);
      });

      // Get all previously guessed letters
      const guessedLetters = new Set();
      state.guesses.forEach((guess) => {
        guess.split('').forEach((letter) => {
          if (letter !== '_') {
            guessedLetters.add(letter.toUpperCase());
          }
        });
      });

      // Filter to letters we can use for hints (letters that appear in the word AND haven't been guessed yet)
      const availableLetters = Object.keys(letterMap).filter(
        (letter) => !guessedLetters.has(letter)
      );

      // If no unguessed letters are available, fall back to all letters in the word
      const lettersToUse =
        availableLetters.length > 0 ? availableLetters : Object.keys(letterMap);

      if (lettersToUse.length > 0) {
        // Randomly select one of the available letters
        const selectedLetter =
          lettersToUse[Math.floor(Math.random() * lettersToUse.length)];

        // Get all positions where this letter appears
        const letterPositions = letterMap[selectedLetter];

        // Create a hint guess with all blank positions except for the hint
        const hintGuess = Array(state.targetWord.length).fill('_');

        // Find a position for the hint letter that is DIFFERENT from its actual positions
        const wrongPositions = [];
        for (let i = 0; i < state.targetWord.length; i++) {
          if (!letterPositions.includes(i)) {
            wrongPositions.push(i);
          }
        }

        // If we have wrong positions available, place the letter in one of them
        if (wrongPositions.length > 0) {
          const hintPosition =
            wrongPositions[Math.floor(Math.random() * wrongPositions.length)];
          hintGuess[hintPosition] = selectedLetter;

          // Add the hint to guesses
          state.guesses.push(hintGuess.join(''));
          state.hintsUsed++;
        }
      }
    },

    resetGame: (state) => {
      const targetWord = state.targetWord; // Keep the same target word
      return {
        ...initialState,
        targetWord,
        currentGuess: Array(targetWord.length).fill(''),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitGuess.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.invalidWord = false;
      })
      .addCase(submitGuess.fulfilled, (state, action) => {
        state.loading = false;
        const { guess, result } = action.payload;
        const { validAttempt, isCorrect, reason } = result;

        // Handle invalid word
        if (reason === INVALID_WORD) {
          state.invalidWord = true;
          return;
        }

        // Handle incomplete answer - do nothing
        if (reason === INCOMPLETE_ANSWER) {
          return;
        }

        // Clear invalid word flag for valid attempts
        state.invalidWord = false;

        // Only add to guesses if it was a valid attempt
        if (validAttempt) {
          state.guesses.push(guess);
          state.gameStats.guessCount++;
        }

        // Check if correct
        if (isCorrect) {
          // Player won!
          state.hasWon = true;
          state.gameStats.completed = true;
        } else {
          // Reset for next guess
          state.currentGuess = Array(state.targetWord.length).fill('');
          state.activeTileIndex = 0;
        }
      })
      .addCase(submitGuess.rejected, (state, action) => {
        state.loading = false;
        const error = action.payload;
        state.error = error?.message || 'Failed to submit guess';
      })
      .addCase(applyHint.pending, (state) => {
        state.error = null;
      })
      .addCase(applyHint.rejected, (state, action) => {
        const error = action.payload;
        state.error = error?.message || 'Failed to use hint';
      });
  },
});

export const {
  setTargetWord,
  setCurrentGuess,
  setActiveTileIndex,
  setInvalidWord,
  handleKeyPress,
  showHint,
  resetGame,
} = learndleWordSlice.actions;

// submitGuess and applyHint are exported as async thunks at the top of the file

export default learndleWordSlice.reducer;
